/*
 * Copyright (c) 2026, WSO2 LLC. (http://www.wso2.com). All Rights Reserved.
 *
 * This software is the property of WSO2 LLC. and its suppliers, if any.
 * Dissemination of any information or reproduction of any material contained
 * herein is strictly forbidden, unless permitted by WSO2 in accordance with
 * the WSO2 Commercial License available at http://wso2.com/licenses.
 * For specific language governing the permissions and limitations under
 * this license, please see the license as well as any agreement you've
 * entered into with WSO2 governing the purchase of this software and any
 * associated services.
 */

import { makeStyles } from '@material-ui/core/styles';

/** Also the fade half of the chip rotation in `CopilotPromptBox`. */
export const CHIPS_FADE_DURATION_MS = 280;

/** Styles for the shared Copilot prompt box. */
export const useCopilotPromptBoxStyles = makeStyles((theme) => ({
  // These replace what would otherwise be `Box` elements carrying system props.
  // A v4 `Box` regenerates a dynamic JSS rule and writes it to the CSSOM on
  // every render, which forces a document wide style recalculation — on the
  // typing path that was the single biggest cost in this component.
  root: {
    display: 'flex',
    flexDirection: 'column',
    height: '100%',
    gap: theme.spacing(2),
  },
  bottomRow: {
    display: 'flex',
    alignItems: 'center',
    justifyContent: 'flex-end',
    gap: theme.spacing(4),
    marginTop: 'auto',
  },
  chipsRow: {
    display: 'flex',
    gap: theme.spacing(1),
    flexWrap: 'nowrap',
    flex: 1,
    minWidth: 0,
    opacity: 1,
    transform: 'translateY(0)',
    transition: `opacity ${CHIPS_FADE_DURATION_MS}ms ease, transform ${CHIPS_FADE_DURATION_MS}ms ease`,
  },
  chipsRowHidden: {
    opacity: 0,
    transform: 'translateY(5px)',
  },
  /**
   * Anchors the submit button's tooltip. A disabled button fires no pointer
   * events, so without an element around it the tooltip stays shut exactly
   * when the visitor is most likely to hover asking why.
   */
  submitButtonWrapper: {
    display: 'inline-flex',
  },
  /**
   * Pulls the button in around the icon now that there is no label beside it:
   * without this it keeps the label-width padding and MUI's 64px minimum, and
   * the arrow floats in the middle of a wide slab. The rounded-rectangle shape
   * and the vertical padding are left alone — this is the same button as
   * before, only without the words.
   *
   * Doubled selector because `Button` puts its own base class on the same
   * element, and which of the two sheets is injected last is not ours to
   * decide.
   */
  submitButton: {
    '&&': {
      minWidth: 0,
      paddingLeft: theme.spacing(1.5),
      paddingRight: theme.spacing(1.5),
    },
  },
  submitIcon: {
    fontSize: theme.spacing(2),
    color: theme.palette.common.white,
    /*
     * The glyph fills its 24x24 box corner to corner with the tip in the top
     * right, so rotating it about the centre swings that tip out past the
     * right edge: the drawn shape ends up spanning roughly 5 to 29 across a
     * box that runs 0 to 24. Equal padding around it then looks left heavy,
     * because a fifth of an em of the arrow is hanging outside the box on the
     * right.
     *
     * The translate puts the drawn shape back over the middle of the box. It
     * is written before the rotation so it applies in screen axes rather than
     * the rotated ones, and it is in `em` so it tracks `fontSize` above.
     */
    transform: 'translateX(-0.2em) rotate(45deg)',
  },
  chatContainer: {
    position: 'relative',
    flex: 1,
    minHeight: theme.spacing(18),
    display: 'flex',
    flexDirection: 'column',
  },
  chatContainerCompact: {
    minHeight: theme.spacing(14),
  },
  chatContainerSmall: {
    minHeight: theme.spacing(10),
  },
  chatTextArea: {
    flex: 1,
    width: '100%',
    minHeight: theme.spacing(18),
    resize: 'none',
    border: 'none',
    // No ring of its own. `:focus-visible` matches a text field however focus
    // was gained — a plain mouse click included — so a ring here drew a second
    // rounded rectangle inside the card the moment the visitor clicked in.
    // The focus indicator a keyboard visitor needs lives on the enclosing card
    // instead, which thickens its existing border on `:focus-within`: one box,
    // and it is the box the visitor is actually typing into.
    outline: 'none',
    fontFamily: theme.typography.fontFamily,
    color: theme.palette.text.primary,
    backgroundColor: 'transparent',
    lineHeight: 1.6,
    zIndex: 1,
    position: 'relative',
  },
  chatTextAreaCompact: {
    minHeight: theme.spacing(9),
  },
  chatTextAreaSmall: {
    minHeight: theme.spacing(6),
  },
  placeholderOverlay: {
    position: 'absolute',
    top: 0,
    left: 0,
    right: 0,
    pointerEvents: 'none',
    padding: `${theme.spacing(0.5)}px 0`,
    zIndex: 0,
  },
  // Sits between the chips and the submit button on the bottom row, and only
  // appears once the input is near the cap.
  characterCount: {
    color: theme.palette.text.secondary,
    whiteSpace: 'nowrap' as const,
    marginLeft: 'auto',
  },
  characterCountAtLimit: {
    color: theme.palette.error.main,
    fontWeight: 600,
  },
  examplePromptChip: {
    cursor: 'pointer',
    border: `1px solid ${theme.palette.grey[200]}`,
    borderRadius: theme.spacing(2),
    padding: '4px 12px',
    color: theme.palette.secondary.main,
    backgroundColor: 'transparent',
    fontFamily: theme.typography.fontFamily,
    lineHeight: 1.5,
    transition: 'all 0.2s ease',
    minWidth: 0,
    whiteSpace: 'nowrap' as const,
    overflow: 'hidden',
    textOverflow: 'ellipsis',
    '&:hover': {
      borderColor: theme.palette.primary.light,
      color: theme.palette.primary.main,
      backgroundColor: theme.custom.indigo[100],
    },
  },
}));

export default useCopilotPromptBoxStyles;
